import { html, nothing, PropertyValueMap, TemplateResult, unsafeCSS } from 'lit'
import { property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'

import { FileItemTemplate } from '@/common/types'
import { DdsElement } from '@/internal/dds-hu-element'
import { SizedMixin } from '@/internal/mixin/sizedMixin'
import { t } from '@/localization/localization'

import DapDSFormLabel from '../form/form-label/form-label.component'
import DapDSIcon from '../icon/icon.component'
import DapDSFileInput from './file-input.component'
import styles from './file-input-list.scss?inline'
import DapDSFileInputListItem from './file-input-list-item.component'

/**
 * `dap-ds-file-input-list`
 * @summary A file input list is a container for file input items.
 * @element dap-ds-file-input-list
 * @title - File input list
 * @group file-input
 *
 * @slot delete-button - The delete button.
 *
 * @event {{ file: FileListElement }} dds-file-click - Dispatched when a file is clicked.
 *
 * @csspart base - The main file input list container.
 *
 * @cssproperty --dds-file-list-display - Controls the display property (default: flex)
 * @cssproperty --dds-file-list-flex-direction - Controls the flex-direction property (default: column)
 * @cssproperty --dds-file-list-padding - Controls the padding property (default: 0)
 * @cssproperty --dds-file-list-list-style - Controls the list-style-type property (default: none)
 * @cssproperty --dds-file-list-gap - Controls the row-gap property (default: var(--dds-spacing-200))
 */
export default class DapDSFileInputList extends SizedMixin(DdsElement) {
  static tagName = 'dap-ds-file-input-list'

  static dependencies = {
    'dap-ds-file-input-list-item': DapDSFileInputListItem,
    'dap-ds-icon': DapDSIcon,
    'dap-ds-form-label': DapDSFormLabel,
  }

  /** The aria label for the file input list. */
  /** The id of the file input element to bind to. */
  @property({ type: String }) for: string

  /** Disables the file input list. */
  @property({ type: Boolean, reflect: true }) disabled = false

  /** The label for the file input list. */
  @property() label?: string

  /** Whether to show the delete button in the file input list item. */
  @property({ type: String }) showDeleteButton = 'true'

  /** Whether to show the file link in the file input list item. */
  @property({ type: String }) showFileLink = 'true'

  /** File item template function
   * @type {(fileItemTemplate: FileItemTemplate) => TemplateResult}
   */
  @property({ attribute: false }) fileItemTemplate?: (
    fileItemTemplate: FileItemTemplate,
  ) => TemplateResult

  /** The label of the file link. */
  @property({ type: String }) fileLinkLabel: string

  /** Whether to emit a custom event with the file data when the file is clicked. */
  @property({ type: Boolean }) emitLinkEvent: boolean = false

  /** Whether to show the file size in the file input list item */
  @property({ type: String }) showFileSize = 'true'

  /** Whether to show thumbnail preview for supported file types
   * @ignore
   */
  @property({ type: String }) showThumbnail = 'false'

  /** Size of the thumbnail preview
   * @ignore
   */
  @property({ type: String })
  thumbnailSize: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' = 'sm'

  @state()
  private _files: TemplateResult[] = []

  static readonly styles = unsafeCSS(styles)

  private fileInputElement: DapDSFileInput | null = null

  get files() {
    return this.querySelectorAll('dap-ds-file-input-list-item')
  }

  getFileByName(name: string) {
    return this.shadowRoot!.querySelector(
      `dap-ds-file-input-list-item[filename="${name}"]`,
    )
  }

  private handleFileClick(event: Event) {
    event.preventDefault()
    event.stopPropagation()
    this.emit('dds-file-click', { data: (event as CustomEvent).detail.data })
  }

  private _handleFilesAccepted(event: Event) {
    const customEvent = event as CustomEvent<{ files: File[] }>
    const files = customEvent.detail.files

    const fileComponents = files.map((file: File) => {
      const linkSlot = this.querySelector('[slot="link"]')
      const hasLinkSlot = linkSlot && linkSlot.innerHTML.trim() !== ''

      return html`<dap-ds-file-input-list-item
        size=${this.size}
        .fileData=${file}
        fileName=${file.name}
        showDeleteButton=${this.showDeleteButton}
        showFileLink=${this.showFileLink}
        fileLinkLabel=${this.fileLinkLabel}
        showFileSize=${this.showFileSize}
        showThumbnail=${this.showThumbnail}
        thumbnailSize=${this.thumbnailSize}
        ?emitLinkEvent=${this.emitLinkEvent}
        .fileItemTemplate=${this.fileItemTemplate}
        @dds-file-click=${this.handleFileClick}
        ?disabled=${this.disabled}>
        <slot name="delete-button" slot="delete-button">
          <dap-ds-icon
            class="content__remove-button-icon"
            name="delete-bin-line"></dap-ds-icon>
        </slot>
        ${hasLinkSlot ? html`<slot name="link" slot="link"></slot>` : nothing}
      </dap-ds-file-input-list-item>`
    })

    this._files = fileComponents
  }

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)
    if (this.for) {
      this.fileInputElement = document.querySelector(`#${this.for}`)
      if (this.fileInputElement) {
        this.fileInputElement.fileList = this
        this.fileInputElement?.addEventListener(
          'dds-files-accepted',
          this._handleFilesAccepted.bind(this),
        )
      }
    }
  }

  removeFile(event: CustomEvent) {
    this.fileInputElement?.dispatchEvent(
      new CustomEvent('dds-file-remove', {
        bubbles: false,
        composed: true,
        detail: event.detail,
      }),
    )
  }

  render() {
    return html`${this._files.length > 0
        ? html`<dap-ds-form-label
            part="label"
            id="label"
            .size=${this.size}
            label=${ifDefined(this.label)}>
          </dap-ds-form-label>`
        : nothing}
      <ul
        class="file-input-list"
        part="base"
        role="list"
        aria-label=${this.ariaLabel ?? t('fileInput.fileList')}
        @dds-file-remove=${this.removeFile}>
        ${this._files}
      </ul>`
  }
}
